import { useEffect, useEffectEvent, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { readLastSiteId } from '@utils/lastSite';
import { useFeatures } from '@permissions/useFeatures';
import { ChevronLeftIcon, ChevronRightIcon } from '@utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { OrcaLogo } from '@components/ui/OrcaLogo/OrcaLogo';
import { HELP_NAV, NAV, resolveTo, type NavCtx, type NavGroupNode } from './nav-config';
import { NavGroup } from './NavGroup/NavGroup';
import { useSidebarCollapse } from './useSidebarCollapse';
import styles from './styles.module.css';

function findActiveGroupId(groups: NavGroupNode[], ctx: NavCtx, pathname: string): string | null {
  for (const group of groups) {
    for (const child of group.children) {
      const patterns = [child.to, ...(child.matchTo ?? [])];
      for (const pattern of patterns) {
        const to = resolveTo(pattern, ctx);
        if (matchPath({ path: to, end: true }, pathname)) {
          return group.id;
        }
      }
    }
  }
  return null;
}

interface ISidebarHeaderProps {
  collapsed: boolean;
  onToggle: () => void;
  /** The expand control — where focus returns when the overlay closes. */
  expandRef: RefObject<HTMLButtonElement | null>;
  /** The collapse control — where focus lands when the overlay opens. */
  collapseRef: RefObject<HTMLButtonElement | null>;
}

function SidebarHeader({ collapsed, onToggle, expandRef, collapseRef }: ISidebarHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <header className={styles.sidebarHeader}>
      <button
        ref={expandRef}
        type="button"
        className={styles.brand}
        onClick={collapsed ? onToggle : () => navigate('/')}
        aria-label={collapsed ? t('nav.expandSidebar') : 'd-orca'}
        tabIndex={collapsed ? 0 : -1}
      >
        <OrcaLogo className={styles.logo} animated={!collapsed} />
        <span className={styles.brandChevron} aria-hidden="true">
          <ChevronRightIcon size={20} />
        </span>
      </button>
      {!collapsed && (
        <div className={styles.brandText} aria-hidden="true">
          <span className={styles.brandProject}>Project Orca</span>
          <span className={styles.brandSuperapp}>SUPERAPP</span>
        </div>
      )}
      {!collapsed && (
        <button
          ref={collapseRef}
          type="button"
          className={styles.collapseToggle}
          onClick={onToggle}
          aria-label={t('nav.collapseSidebar')}
          title={t('nav.collapseSidebar')}
        >
          <ChevronLeftIcon size={16} />
        </button>
      )}
    </header>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const ctx: NavCtx = { lastSiteId: readLastSiteId() ?? undefined };
  const features = useFeatures();
  const visibleNav = useMemo<NavGroupNode[]>(() => {
    return NAV
      .map((group) => ({
      ...group,
      children: group.children.filter((c) => !c.feature || features.has(c.feature)),
      }))
      .filter((group) => group.children.length > 0);
  }, [features]);
  const activeGroupId = findActiveGroupId([...visibleNav, HELP_NAV], ctx, location.pathname);
  const [openId, setOpenId] = useState<string | null>(activeGroupId);
  const { collapsed, overlay, setCollapsed } = useSidebarCollapse();

  const [prevActiveId, setPrevActiveId] = useState<string | null>(activeGroupId);

  if (activeGroupId !== prevActiveId) {
    setPrevActiveId(activeGroupId);
    setOpenId(activeGroupId);
  }

  const handleToggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      setOpenId(next ? null : activeGroupId);
      return next;
    });
  };

  /* The narrow-viewport overlay behaves as a modal dialog: focus moves into it
     on open and back to the expand control on close, Tab stays inside it, and
     Escape closes it. */
  const asideRef = useRef<HTMLElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);
  const collapseRef = useRef<HTMLButtonElement>(null);
  const wasOverlay = useRef(false);
  const closeOverlay = useEffectEvent(() => handleToggleCollapsed());

  useEffect(() => {
    if (overlay) collapseRef.current!.focus();
    else if (wasOverlay.current) expandRef.current!.focus();
    wasOverlay.current = overlay;
    if (!overlay) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') closeOverlay();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [overlay]);

  const keepFocusInOverlay = (event: KeyboardEvent<HTMLElement>) => {
    if (!overlay || event.key !== 'Tab') return;
    const focusable = asideRef.current!.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    // Never empty: an open overlay always holds its collapse control and nav.
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleGroupToggle = (id: string) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenId(id);
      return;
    }
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <>
      {overlay && (
        <div
          className={styles.backdrop}
          data-testid="sidebar-backdrop"
          aria-hidden="true"
          onClick={handleToggleCollapsed}
        />
      )}
      <aside
        ref={asideRef}
        className={[styles.sidebar, overlay && styles.sidebarOverlay].filter(Boolean).join(' ')}
        aria-label={t('nav.primary')}
        role={overlay ? 'dialog' : undefined}
        aria-modal={overlay || undefined}
        onKeyDown={keepFocusInOverlay}
      >
        <ScrollArea
          className={styles.sidebarScroll}
          viewportClassName={[styles.sidebarBody, collapsed && styles.sidebarCollapsed]
            .filter(Boolean)
            .join(' ')}
        >
          <SidebarHeader
            collapsed={collapsed}
            onToggle={handleToggleCollapsed}
            expandRef={expandRef}
            collapseRef={collapseRef}
          />
          <nav className={styles.nav}>
            {visibleNav.map((group) => (
              <NavGroup
                key={group.id}
                group={group}
                ctx={ctx}
                expanded={group.id === openId}
                active={activeGroupId === group.id}
                collapsed={collapsed}
                onToggle={() => handleGroupToggle(group.id)}
              />
            ))}
          </nav>
          <div className={styles.helpSection}>
            <NavGroup
              group={HELP_NAV}
              ctx={ctx}
              expanded={HELP_NAV.id === openId}
              active={activeGroupId === HELP_NAV.id}
              collapsed={collapsed}
              onToggle={() => handleGroupToggle(HELP_NAV.id)}
            />
          </div>
        </ScrollArea>
      </aside>
    </>
  );
}
